<template>
  <v-form>
    <v-card flat>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-btn small text @click="setSelectedDateRange(0)">
              {{ $vuetify.lang.t('$vuetify.FROM_PREVIOUS_MONTH_START') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(1)">
              {{ $vuetify.lang.t('$vuetify.PREVIOUS_MONTH') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(2)">
              {{ $vuetify.lang.t('$vuetify.CURRENT_MONTH') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(3)">
              {{ $vuetify.lang.t('$vuetify.PREVIOUS_WEEK') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(4)">
              {{ $vuetify.lang.t('$vuetify.CURRENT_WEEK') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(5)">
              {{ $vuetify.lang.t('$vuetify.YESTERDAY') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(6)">
              {{ $vuetify.lang.t('$vuetify.TODAY') }}
            </v-btn>
            <v-btn small text @click="setSelectedDateRange(7)">
              {{ $vuetify.lang.t('$vuetify.LAST_7_DAYS') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="selectedReportType"
              :items="reportTypes"
              item-value="code"
              :label="$vuetify.lang.t('$vuetify.EDIT_WHITELISTS.TYPE')"
              outlined
              hide-details
              :menu-props="{
                bottom: true,
                offsetY: true
              }"
            >
              <template v-slot:selection="{ item }">
                {{ $vuetify.lang.t(`$vuetify.${item.translation}`) }}
              </template>
              <template v-slot:item="{ item }">
                {{ $vuetify.lang.t(`$vuetify.${item.translation}`) }}
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-menu
              v-model="validFromPicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="datePickerFormat(selectedValidFrom)"
                  :label="$vuetify.lang.t('$vuetify.STATISTICS.FROM')"
                  :placeholder="$vuetify.lang.t('$vuetify.STATISTICS.FROM')"
                  append-icon="mdi-calendar"
                  readonly
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  :title-date-format="formatDate"
                  @click:clear="selectedValidFrom = null"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="selectedValidFrom"
                @input="validFromPicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-menu
              v-model="validToPicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="datePickerFormat(selectedValidTo)"
                  :label="$vuetify.lang.t('$vuetify.STATISTICS.TO')"
                  :placeholder="$vuetify.lang.t('$vuetify.STATISTICS.TO')"
                  append-icon="mdi-calendar"
                  readonly
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  @click:clear="selectedValidTo = null"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="selectedValidTo"
                @input="validToPicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="licencePlate"
              :label="$vuetify.lang.t('$vuetify.EDIT_WHITELISTS.CAR_NUMBER')"
              outlined
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="8">
            <v-autocomplete
              :items="sortedWhitelists"
              item-text="Name"
              return-object
              :label="
                $vuetify.lang.t('$vuetify.EDIT_WHITELISTS.SELECT_WHITELIST')
              "
              :loading="loading.state && loading.type === 'getWhitelists'"
              :value="selectedWhitelist.Name"
              outlined
              hide-details
              :menu-props="{
                bottom: true,
                offsetY: true
              }"
              @change="selectedWhitelist = $event"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              :disabled="!selectedWhitelist"
              :loading="loading.state && loading.type === 'getReportRows'"
              color="white"
              class="success--text mr-3"
              :class="$vuetify.breakpoint.smAndDown ? 'mb-3' : ''"
              @click="generateWhitelistReport()"
            >
              {{ $vuetify.lang.t('$vuetify.GENERATE') }}
            </v-btn>
            <v-btn
              :disabled="!selectedWhitelist"
              :loading="loading.state && loading.type === 'getWhitelistXLSX'"
              color="white"
              class="success--text"
              :class="$vuetify.breakpoint.smAndDown ? 'mb-3' : ''"
              @click="getWhitelistReportXlsxXFile()"
            >
              <v-icon class="mr-2">mdi-microsoft-excel</v-icon>
              {{ $vuetify.lang.t('$vuetify.EXPORT_TO_EXCEL') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-data-iterator
          v-if="totalInfo"
          content-tag="tag"
          row
          wrap
          :items="[totalInfo]"
          rows-per-page="1"
          hide-default-footer=""
        >
          <template v-slot:default="props">
            <v-row>
              <v-col
                v-for="item in props.items"
                :key="item.name"
                cols="12"
                sm="6"
                md="5"
                lg="5"
              >
                <v-card>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        {{
                          $vuetify.lang.t(
                            '$vuetify.EDIT_WHITELISTS.SESSIONS_COUNT'
                          )
                        }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.SessionsCount }}
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        {{ $vuetify.lang.t('$vuetify.PARKED_MINUTES') }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.ParkingMinutes }}
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item v-if="selectedReportType !== 'SESSIONS_SELF_PAID'">
                      <v-list-item-content>
                        {{ $vuetify.lang.t('$vuetify.OVERTIME_MINUTES') }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.PenaltyMinutes + item.HiddenPaidMinutes }}
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item v-if="selectedReportType !== 'SESSIONS_SELF_PAID'">
                      <v-list-item-content>
                        {{
                          $vuetify.lang.t(
                            '$vuetify.EDIT_WHITELISTS.OVER_ALLOWED_SPOTS_LIMIT_NUMBER_OF_PARKING_SESSIONS'
                          )
                        }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.CountOfSessionsWithExceededSpotsLimit }}
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item v-if="selectedReportType !== 'SESSIONS_SELF_PAID'">
                      <v-list-item-content>
                        {{
                          $vuetify.lang.t(
                            '$vuetify.EDIT_WHITELISTS.OVER_ALLOWED_SPOTS_LIMIT_MINUTES'
                          )
                        }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.ExceededWhitelistSpotsLimitParkingMinutes }}
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        {{ $vuetify.lang.t('$vuetify.EDIT_WHITELISTS.COST') }}
                      </v-list-item-content>
                      <v-list-item-content class="align-end">
                        {{ item.HiddenCostInCents / 100 }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </v-data-iterator>
        <v-row v-if="reportRows && reportRows.length">
          <v-col cols="12">
            <v-data-table
              v-if="selectedReportType === 'SESSIONS'"
              :headers="[
                {
                  text: 'Session ID',
                  align: 'start',
                  sortable: false,
                  value: 'ParkingSessionId'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.LOCATION'),
                  value: 'PlaceName'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.NAME'),
                  value: 'ClientName'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.PHONE_NUMBER'),
                  value: 'ClientPhone'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.CAR_NUMBER'),
                  value: 'CarNumber'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.START_TIME'),
                  value: 'StartTime'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.END_TIME'),
                  value: 'EndTime'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.COMMENTS'),
                  value: 'Comments'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.OVERTIME_MINUTES'
                  ),
                  value: 'PenaltyMinutes'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.EDIT_WHITELISTS.COST'),
                  value: 'CostInCents'
                }
              ]"
              :items="reportRows"
              class="elevation-1"
              hide-default-footer
              :items-per-page="
                reportRows && reportRows.length ? reportRows.length : 0
              "
            >
              <template v-slot:item="{ item }">
                <tr
                  :class="{ pink: item.ExceededWhitelistSpotsLimit === true }"
                >
                  <td>{{ item.ParkingSessionId }}</td>
                  <td>{{ item.PlaceName }}</td>
                  <td>{{ item.ClientName }}</td>
                  <td>{{ item.ClientPhone }}</td>
                  <td>{{ item.CarNumber }}</td>
                  <td>{{ dateEE(item.StartTime) }}</td>
                  <td>{{ dateEE(item.EndTime) }}</td>
                  <td>{{ item.Comments }}</td>
                  <td>
                    <span
                      v-if="item.PenaltyMinutes || item.HiddenPaidMinutes"
                      class="red--text"
                    >
                      {{ item.PenaltyMinutes + item.HiddenPaidMinutes }}
                    </span>
                  </td>
                  <td>
                    <span v-if="item.HiddenCostInCents">
                      {{ item.HiddenCostInCents / 100 }}
                      {{ item.Currency }}
                    </span>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-data-table
              v-if="selectedReportType === 'SESSIONS_GROUPED_BY_CAR'"
              dense
              :headers="[
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.CAR_NUMBER'
                  ),
                  value: 'CarNumber'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.COMMENTS'
                  ),
                  value: 'Comments'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.NAME'),
                  value: 'ClientName'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.PHONE_NUMBER'
                  ),
                  value: 'ClientPhone'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.SESSIONS_COUNT'
                  ),
                  value: 'SessionsCount'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.PARKED_MINUTES'
                  ),
                  value: 'ParkingMinutes'
                },
                {
                  text: this.$vuetify.lang.t(
                    '$vuetify.EDIT_WHITELISTS.OVERTIME_MINUTES'
                  ),
                  value: 'PenaltyMinutes'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.EDIT_WHITELISTS.COST'),
                  value: 'OverTimeCostInCents'
                }
              ]"
              :items="reportRows"
              class="elevation-1"
              hide-default-footer
              :items-per-page="
                reportRows && reportRows.length ? reportRows.length : 0
              "
            >
              <template v-slot:item.StartTime="props">
                <td>{{ dateEE(props.item.StartTime) }}</td>
              </template>
              <template v-slot:item.EndTime="props">
                <td>{{ dateEE(props.item.EndTime) }}</td>
              </template>
              <template v-slot:item.PenaltyMinutes="props">
                <td>
                  <span
                    v-if="
                      props.item.PenaltyMinutes || props.item.HiddenPaidMinutes
                    "
                    class="red--text"
                  >
                    {{
                      props.item.PenaltyMinutes + props.item.HiddenPaidMinutes
                    }}
                  </span>
                </td>
              </template>
              <template v-slot:item.CostInCents="props">
                <td v-if="props.item.PenaltyMinutes">
                  {{ props.item.PenaltyMinutes }}
                  {{ props.item.Currency }}
                </td>
                <td v-if="props.item.HiddenCostInCents">
                  {{ props.item.HiddenCostInCents / 100 }}
                  {{ props.item.Currency }}
                </td>
              </template>
              <template v-slot:item.OverTimeCostInCents="props">
                <td>
                  {{ props.item.OverTimeCostInCents / 100 }}
                </td>
              </template>
            </v-data-table>
            <v-data-table
              v-if="selectedReportType === 'SESSIONS_SELF_PAID'"
              :headers="[
                {
                  text: 'Session ID',
                  align: 'start',
                  sortable: false,
                  value: 'ParkingSessionId'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.LOCATION'),
                  value: 'PlaceName'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.NAME'),
                  value: 'ClientName'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.PHONE_NUMBER'),
                  value: 'ClientPhone'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.CAR_NUMBER'),
                  value: 'CarNumber'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.START_TIME'),
                  value: 'StartTime'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.END_TIME'),
                  value: 'EndTime'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.COMMENTS'),
                  value: 'Comments'
                },
                {
                  text: this.$vuetify.lang.t('$vuetify.EDIT_WHITELISTS.COST'),
                  value: 'CostInCents'
                }
              ]"
              :items="reportRows"
              class="elevation-1"
              hide-default-footer
              :items-per-page="
                reportRows && reportRows.length ? reportRows.length : 0
              "
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.ParkingSessionId }}</td>
                  <td>{{ item.PlaceName }}</td>
                  <td>{{ item.ClientName }}</td>
                  <td>{{ item.ClientPhone }}</td>
                  <td>{{ item.CarNumber }}</td>
                  <td>{{ dateEE(item.StartTime) }}</td>
                  <td>{{ dateEE(item.EndTime) }}</td>
                  <td>{{ item.Comments }}</td>
                  <td>
                    <span v-if="item.CostInCents">
                      {{ item.CostInCents / 100 }}
                      {{ item.Currency }}
                    </span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions
        v-if="reportRows && reportRows.length"
        class="d-flex justify-center align-items-center"
      >
        <div
          v-show="
            currentPageNumber - 1 > 0 ||
              currentPageNumber - 1 < nextAvailablePage
          "
        >
          <div class="">
            <v-btn
              v-show="currentPageNumber - 1 > 0"
              :loading="loading.state && loading.type === 'getReportRows'"
              text
              @click="previousPage()"
            >
              <v-icon>mdi-menu-left</v-icon>
              {{ $vuetify.lang.t('$vuetify.PREVIOUS') }}
            </v-btn>
            <v-btn
              v-show="currentPageNumber - 1 < nextAvailablePage"
              class="btn btn-sm btn-default"
              :loading="loading.state && loading.type === 'getReportRows'"
              text
              @click="
                currentPageNumber = nextAvailablePage + 1
                generateWhitelistReport()
              "
            >
              {{ $vuetify.lang.t('$vuetify.NEXT') }}
              <v-icon>mdi-menu-right</v-icon>
            </v-btn>
          </div>
          <div class="d-flex justify-center align-items-center mt-2 mr-2">
            <span translate class="text-uppercase">{{
              $vuetify.lang.t('$vuetify.PAGE')
            }}</span>
            :{{ currentPageNumber }}
          </div>
        </div>
      </v-card-actions>
    </v-card>
  </v-form>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import formatDate from '@/mixins/formatDate'

export default {
  name: 'WhitelistReport',
  mixins: [formatDate],
  computed: {
    ...mapState('whitelists', ['nextAvailablePage']),
    ...mapGetters('app', ['loading']),
    ...mapGetters('whitelists', ['sortedWhitelists']),
    currentPageNumber: {
      get() {
        return this.$store.state.app.currentPageNumber
      },
      set(val) {
        this.$store.commit('app/setCurrentPageNumber', val)
      }
    }
  },
  data() {
    return {
      selectedWhitelist: '',
      reportTypes: [
        {
          title: 'Sessions',
          code: 'SESSIONS',
          translation: 'STATISTICS.PARKING_SESSIONS'
        },
        {
          title: 'Group by car',
          code: 'SESSIONS_GROUPED_BY_CAR',
          translation: 'STATISTICS.GROUP_BY_CAR'
        },
        {
          title: 'Self-paid sessions',
          code: 'SESSIONS_SELF_PAID',
          translation: 'STATISTICS.SELF_PAID_PARKING_SESSIONS'
        }
      ],
      selectedReportType: 'SESSIONS',
      validFromPicker: false,
      validToPicker: false,
      selectedValidFrom: '',
      selectedValidTo: '',
      licencePlate: '',
      reportRows: [],
      totalInfo: null
    }
  },
  watch: {
    selectedReportType() {
      this.reportRows = []
      this.totalInfo = null
    },
    sortedWhitelists() {
      // Eslint rule does not match this use case very well.
      // eslint-disable-next-line prefer-destructuring
      this.selectedWhitelist = this.sortedWhitelists[0]
    }
  },
  created() {
    const currentDate = new Date()
    this.selectedValidFrom = this.formatDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
    )
    this.selectedValidTo = this.formatDate(currentDate)
  },
  mounted() {
    if (!this.sortedWhitelists || !this.sortedWhitelists.length) {
      this.getWhitelists({
        includeAvailabilityInfo: true,
        withAvailabilityInfo: false,
        queryString: `&page=${this.currentPageNumber}&page_size=${this.pageSize}`
      })
    } else {
      // eslint-disable-next-line prefer-destructuring
      this.selectedWhitelist = this.sortedWhitelists[0]
    }
  },
  methods: {
    ...mapActions('whitelists', ['getWhitelists', 'getWhitelistXLSX']),
    formatDate(date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    },
    datePickerFormat(dateValue) {
      const date = new Date(dateValue)
      return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
    },
    formatDateEE(value) {
      const year = new Date(value).getFullYear()
      const month = new Date(value).getMonth() + 1
      const date = new Date(value).getDate()
      if (value) {
        return `${date}.${month}.${year}`
      }
      return null
    },
    getWhitelistReportXlsxXFile() {
      this.getWhitelistXLSX({
        id: this.selectedWhitelist.ID,
        validFrom: this.formatDateEE(this.selectedValidFrom, true, '.'),
        validTo: this.formatDateEE(this.selectedValidTo, true, '.'),
        type: this.selectedReportType,
        licencePlate: this.licencePlate
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `${this.selectedWhitelist.Name}-${this.selectedMonth ||
            this.selectedValidFrom}-${this.selectedYear ||
            this.selectedValidTo}.xlsx`
        )
        document.body.appendChild(link)
        link.click()
        link.remove()
      })
    },
    generateWhitelistReport() {
      this.reportRows = []
      this.$store
        .dispatch('whitelists/getReportRows', {
          id: this.selectedWhitelist.ID,
          from: this.formatDateEE(this.selectedValidFrom, true, '.'),
          to: this.formatDateEE(this.selectedValidTo, true, '.'),
          type: this.selectedReportType,
          licencePlate: this.licencePlate
        })
        .then(data => {
          if (this.selectedReportType === 'SESSIONS') {
            this.reportRows = data.Sessions
          } else if (this.selectedReportType === 'SESSIONS_GROUPED_BY_CAR') {
            this.reportRows = data.Rows
          } else if (this.selectedReportType === 'SESSIONS_SELF_PAID') {
            this.reportRows = data.Sessions
          }

          if (this.currentPageNumber === 1) {
            this.totalInfo = data.Totals
          }
        })
    },
    previousPage() {
      if (this.currentPageNumber - 1 > 0) {
        this.currentPageNumber -= 1
        this.generateWhitelistReport()
      }
    },
    getPreviousMonday() {
      const beforeOneWeek = new Date(
        new Date().getTime() - 60 * 60 * 24 * 7 * 1000
      )
      const day = beforeOneWeek.getDay()
      const diffToMonday = beforeOneWeek.getDate() - day + (day === 0 ? -6 : 1)
      const lastMonday = new Date(beforeOneWeek.setDate(diffToMonday))

      return this.formatDate(lastMonday)
    },
    setSelectedDateRange(option) {
      const date = new Date()
      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth()
      const lastMonth = date.getMonth() - 1
      const weekstart = date.getDate() - date.getDay() + 1
      const weekend = weekstart + 4
      const monday = new Date(date.setDate(weekstart))
      const sunday = new Date(date.setDate(weekend))

      switch (option) {
        case 0: // Eelmise kuu algusest
          this.selectedValidFrom = this.formatDate(
            new Date(currentYear, lastMonth, 1)
          )
          this.selectedValidTo = this.formatDate(new Date())
          break
        case 1: // Eelmine kuu
          this.selectedValidFrom = this.formatDate(
            new Date(currentYear, lastMonth, 1)
          )
          this.selectedValidTo = this.formatDate(
            new Date(currentYear, currentMonth, 0)
          )
          break
        case 2: // Jooksev kuu
          this.selectedValidFrom = this.formatDate(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          )
          this.selectedValidTo = this.formatDate(new Date())
          break
        case 3: // Eelmine nädal
          this.selectedValidFrom = this.getPreviousMonday()
          this.selectedValidTo = this.formatDate(
            new Date(
              date.setTime(
                date.getTime() -
                  (date.getDay() ? date.getDay() : 7) * 24 * 60 * 60 * 1000
              )
            )
          )
          break
        case 4: // Jooksev nädal
          this.selectedValidFrom = this.formatDate(monday)
          this.selectedValidTo = this.formatDate(sunday)
          break
        case 5: // Eile
          this.selectedValidFrom = this.formatDate(new Date(Date.now() - 864e5)) // 864e5 == 86400000 == 24*60*60*1000
          this.selectedValidTo = this.formatDate(new Date(Date.now() - 864e5)) // 864e5 == 86400000 == 24*60*60*1000
          break
        case 6: // Täna
          this.selectedValidFrom = this.formatDate(new Date())
          this.selectedValidTo = this.formatDate(new Date())
          break
        case 7: // Viimased 7 päeva
          this.selectedValidFrom = this.formatDate(
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          )
          this.selectedValidTo = this.formatDate(new Date())
          break

        default:
          break
      }
    }
  }
}
</script>

<style scoped>
.pink {
  background-color: pink;
}
</style>
